/**
 * Participant ID of each observed remote tile element.
 */
const _tiles = new Map();

/**
 * Participants whose tile intersects the viewport.
 */
const _onScreen = new Set();

let _observer;
let _onChange;

/**
 * Returns the remote participants whose tile is on screen, or undefined where
 * tiles are not tracked (React Native, or before the first tile mounts).
 *
 * @returns {Set<string>|undefined}
 */
export function getOnScreenTiles() {
    return _observer ? _onScreen : undefined;
}

/**
 * Sets the function called when the set of on-screen tiles changes.
 *
 * @param {Function} onChange - The listener.
 * @returns {void}
 */
export function setOnScreenTilesListener(onChange) {
    _onChange = onChange;
}

/**
 * Starts tracking whether a remote tile intersects the viewport. Tiles hidden
 * with display: none never intersect. A no-op on React Native.
 *
 * @param {string} id - The participant ID.
 * @param {HTMLElement} element - The tile element.
 * @returns {void}
 */
export function observeTile(id, element) {
    if (!element || typeof IntersectionObserver === 'undefined') {
        return;
    }

    _observer = _observer ?? new IntersectionObserver(entries => {
        let changed = false;

        for (const { isIntersecting, target } of entries) {
            const tileId = _tiles.get(target);

            // Entries queued before unobserve() can still arrive.
            if (tileId !== undefined && isIntersecting !== _onScreen.has(tileId)) {
                isIntersecting ? _onScreen.add(tileId) : _onScreen.delete(tileId);
                changed = true;
            }
        }

        changed && _onChange?.();
    });
    _tiles.set(element, id);
    _observer.observe(element);
}

/**
 * Stops tracking the tile of a participant.
 *
 * @param {string} id - The participant ID.
 * @returns {void}
 */
export function unobserveTile(id) {
    for (const [ element, tileId ] of _tiles) {
        if (tileId === id) {
            _observer.unobserve(element);
            _tiles.delete(element);
        }
    }

    _onScreen.delete(id) && _onChange?.();
}
